import { Link, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { NavigationGroup, NavigationItem } from '@xxtract/ui';
import { useEffectiveAccess } from '@/lib/effectiveAccess';
import { hasAnyPermission, WERKKWALITEIT_PERMISSIONS } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { MIJN_FEEDBACK_PAD } from '@/lib/landing';
import { werkkwaliteitStart } from '@/features/work-quality/tabs';
import { LAGEN } from '@xxtract/ui';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  to: string;
  icon: string;
  labelKey: string;
  testId: string;
  matches?: (pathname: string) => boolean;
  /** Hidden for anyone below administrator — the screen behind it is admin-only. */
  adminOnly?: boolean;
  /** Hidden for anyone below manager — same reason, one rank down (story 28-3, besluit B45). */
  managerOnly?: boolean;
  /**
   * Story 4.1 / AD-15: hidden unless the session holds at least one of these permission names.
   * The first item in this portal that hangs on a right rather than on a rank. It is not a
   * safeguard — the API decides — but the navigation must not spell out who holds which right,
   * so the item looks identical for everyone who does see it.
   */
  requiresAnyPermission?: readonly string[];
}

const matchesPathPrefix = (path: string) => (pathname: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

const customerItems: NavItem[] = [
  { to: '/dashboard', icon: 'dashboard', labelKey: 'nav.dashboard', testId: 'nav-dashboard' },
  { to: '/products', icon: 'package_2', labelKey: 'nav.products', testId: 'nav-products', matches: matchesPathPrefix('/products') },
  { to: '/intake', icon: 'upload_file', labelKey: 'nav.intake', testId: 'nav-intake', matches: matchesPathPrefix('/intake') },
  { to: '/media', icon: 'perm_media', labelKey: 'nav.media', testId: 'nav-media', matches: matchesPathPrefix('/media') },
  { to: '/invoices', icon: 'receipt_long', labelKey: 'nav.invoices', testId: 'nav-invoices', matches: matchesPathPrefix('/invoices') },
  { to: '/reports', icon: 'bar_chart', labelKey: 'nav.reports', testId: 'nav-reports', matches: matchesPathPrefix('/reports') },
];

/**
 * CAP-6: het hele menu van de rol employee. Eén item, naar de bestaande eigenpagina van
 * Werkkwaliteit; alles wat de server een employee weigert staat hier niet. Het hangt bewust niet aan
 * een recht: een employee zonder het eigen recht landt toch op deze pagina en krijgt daar de
 * melding, in plaats van op een leeg menu.
 */
const feedbackItems: NavItem[] = [
  { to: MIJN_FEEDBACK_PAD, icon: 'query_stats', labelKey: 'nav.myFeedback', testId: 'nav-my-feedback', matches: matchesPathPrefix(MIJN_FEEDBACK_PAD) },
];

const internalItems: NavItem[] = [
  { to: '/orders', icon: 'inventory_2', labelKey: 'nav.orders', testId: 'nav-orders', matches: matchesPathPrefix('/orders') },
  // h6-1/Story 1.1: nav-plaatsing "tussen Orders en Klanten" + icoon account_tree —
  // [ASSUMPTION, bevestigen met UX/PO]. Sectie "Werkvoorraad" bestaat nog niet als aparte
  // NavigationGroup in deze sidebar (alleen "Beheer" is een group); processes staat daarom
  // vlak, net als orders/clients hierboven.
  { to: '/processes', icon: 'account_tree', labelKey: 'nav.processes', testId: 'nav-processes', matches: matchesPathPrefix('/processes') },
  { to: '/clients', icon: 'groups', labelKey: 'nav.clients', testId: 'nav-clients' },
  { to: '/data-quality', icon: 'fact_check', labelKey: 'nav.dataQuality', testId: 'nav-data-quality', matches: matchesPathPrefix('/data-quality') },
  // Story 4.1, beslecht punt 2 in de spine: direct na Datakwaliteit, en dus vóór het
  // kwartaalrapport en Behandeltijd. De naam is bewust Werkkwaliteit en niet Datakwaliteit —
  // dat bestaande item gaat over de data van klanten, dit over ons eigen werk.
  { to: '/werkkwaliteit', icon: 'query_stats', labelKey: 'nav.werkkwaliteit', testId: 'nav-werkkwaliteit', matches: matchesPathPrefix('/werkkwaliteit'), requiresAnyPermission: WERKKWALITEIT_PERMISSIONS },
  { to: '/reports/quarterly', icon: 'bar_chart', labelKey: 'nav.reports', testId: 'nav-quarterly-report', matches: matchesPathPrefix('/reports/quarterly') },
  // Story 28-3: alleen zichtbaar voor de leidinggevende en de beheerder. Zonder deze begrenzing
  // houdt een medewerker een menu-item dat hem regelrecht terugstuurt naar het dashboard.
  { to: '/behandeltijd', icon: 'timer', labelKey: 'nav.handlingTime', testId: 'nav-handling-time', matches: matchesPathPrefix('/behandeltijd'), managerOnly: true },
];

// `adminOnly` items are hidden for anyone below administrator. Without this an employee keeps
// a menu item that bounces him straight back to the dashboard — exactly what /admin/settings
// already did before besluit 5 (migratieplan portaalrollen, 2026-08-23).
const managementItems: NavItem[] = [
  { to: '/users', icon: 'group', labelKey: 'nav.users', testId: 'nav-users', adminOnly: true },
  { to: '/regelinzicht', icon: 'rule', labelKey: 'nav.regelinzicht', testId: 'nav-regelinzicht' },
  { to: '/admin/mapping-profiles', icon: 'account_tree', labelKey: 'nav.mappingProfiles', testId: 'nav-mapping-profiles' },
  { to: '/admin/settings', icon: 'settings', labelKey: 'nav.settings', testId: 'nav-settings', adminOnly: true },
  { to: '/admin/analytics', icon: 'analytics', labelKey: 'nav.analytics', testId: 'nav-analytics' },
];

function isCurrent(item: NavItem, pathname: string): boolean {
  return item.matches?.(pathname) ?? pathname === item.to;
}

interface Toegang {
  isAdmin: boolean;
  isManager: boolean;
  permissions: readonly string[];
}

/**
 * One gate for every list, so a menu item cannot lose its restriction by being moved between
 * `internalItems` and `managementItems`. All three demands are independent: a rank does not hand
 * out a right and a right does not promote a rank (NFR11).
 */
function isVisible(item: NavItem, { isAdmin, isManager, permissions }: Toegang): boolean {
  if (item.adminOnly && !isAdmin) return false;
  if (item.managerOnly && !isManager) return false;
  if (item.requiresAnyPermission && !hasAnyPermission(permissions, item.requiresAnyPermission)) return false;
  return true;
}

function NavigationLink({ item, onClose, topLevel = false }: { item: NavItem; onClose: () => void; topLevel?: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const current = isCurrent(item, pathname);

  return (
    <NavigationItem active={current}>
      {({ className, ...state }) => (
        // `Link` en niet `NavLink`: of een item actief is, beslist `isCurrent`. NavLink zet
        // `aria-current` alleen bij zijn eigen `to`, en Werkkwaliteit wijst naar Team terwijl het
        // item op elk werkkwaliteitsadres actief hoort te zijn (CAP-10).
        <Link
          to={item.to}
          data-testid={item.testId}
          data-nav-level={topLevel ? 'top' : undefined}
          onClick={onClose}
          aria-current={current ? 'page' : undefined}
          className={className}
          {...state}
        >
          <span className="material-symbols-outlined shrink-0 text-[17px]" aria-hidden="true">
            {item.icon}
          </span>
          {t(item.labelKey)}
        </Link>
      )}
    </NavigationItem>
  );
}

function ManagementNavigation({ onClose, toegang }: { onClose: () => void; toegang: Toegang }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const visibleItems = managementItems.filter((item) => isVisible(item, toegang));
  const isManagementRoute = visibleItems.some((item) => isCurrent(item, pathname));

  return (
    <NavigationGroup testId="nav-management" navLevel="top" ariaLabel={t('nav.management')} label={t('nav.management')} icon={<span className="material-symbols-outlined shrink-0 text-[17px]" aria-hidden="true">settings</span>} open={isManagementRoute}>
      <div className="mt-1" aria-label={t('nav.management')}>
        {visibleItems.map((item) => <NavigationLink key={item.to} item={item} onClose={onClose} />)}
      </div>
    </NavigationGroup>
  );
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const { role, permissions } = useEffectiveAccess();
  const isFeedbackOnly = role === 'employee';
  const isInternal = role === 'admin' || role === 'manager';
  const isAdmin = role === 'admin';
  // De rangorde van ProtectedRoute, één rang lager: de beheerder erft wat de leidinggevende mag.
  const isManager = role === 'admin' || role === 'manager';
  // `permissions` is tijdens klantsimulatie al leeg (AD-15), dus een beheerder die een klant
  // bekijkt houdt geen enkel op een recht hangend menu-item over.
  const toegang: Toegang = { isAdmin, isManager, permissions };

  return (
    <>
      {isOpen && <div data-testid="sidebar-backdrop" className={cn('fixed inset-0 bg-black/30 xl:hidden', LAGEN.zijbalkWaas)} onClick={onClose} aria-hidden="true" />}
      <aside
        data-testid="sidebar"
        className={cn(
          'fixed top-[3.5rem] left-0 h-[calc(100vh-3.5rem)] w-60 overflow-y-auto border-r border-[var(--border)] bg-[var(--background)] transition-transform duration-200 ease-in-out xl:sticky xl:translate-x-0',
          LAGEN.zijbalk,
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <nav className="py-3" aria-label={t('nav.mainNavigation')}>
          {(isFeedbackOnly ? feedbackItems : isInternal ? internalItems : customerItems)
            .filter((item) => isVisible(item, toegang))
            // CAP-10: Werkkwaliteit opent Team voor wie het team mag zien, anders Mijn feedback.
            .map((item) => (item.testId === 'nav-werkkwaliteit' ? { ...item, to: werkkwaliteitStart(permissions) } : item))
            .map((item) => (
            <NavigationLink key={item.to} item={item} onClose={onClose} topLevel />
          ))}
          {isInternal && <ManagementNavigation onClose={onClose} toegang={toegang} />}
        </nav>
      </aside>
    </>
  );
}
